import React, { useEffect, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    getDashboardSummary,
} from "./api";
import { buildPortalHash } from "./betaRouting";
import {
    BetaDashboardSection,
    BetaDashboardStats,
    BetaDashboardTable,
    getBetaDashboardStatusTone,
} from "./BetaDashboardUI";

// ---- DATE FORMATTER ----
const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d; // fallback

    return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const toLocalYmd = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const formatPublishDateLabel = (d) => {
    if (!d) return "";
    const text = String(d).slice(0, 10);
    const dt = new Date(`${text}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return d;

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (text === toLocalYmd(today)) return "Today";
    if (text === toLocalYmd(tomorrow)) return "Tomorrow";
    return formatDate(text);
};

const getOrdinalSuffix = (day) => {
    const value = Number(day);
    if (!Number.isFinite(value)) return "";
    const mod100 = value % 100;
    if (mod100 >= 11 && mod100 <= 13) return "th";
    switch (value % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};

const formatScheduleDayLabel = (dateOnly, fallback = "") => {
    const text = String(dateOnly || "").slice(0, 10);
    const dt = new Date(`${text}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return fallback || text;
    const weekday = dt.toLocaleDateString("en-GB", { weekday: "long" });
    const month = dt.toLocaleDateString("en-GB", { month: "long" });
    const day = dt.getDate();
    return `${weekday} ${day}${getOrdinalSuffix(day)} ${month}`;
};

const formatEpisodeCodeLabel = (seasonNumber, episodeNumber) => {
    const episode = Number.parseInt(episodeNumber, 10);
    if (!Number.isFinite(episode) || episode <= 0) return "";

    const season = Number.parseInt(seasonNumber, 10);
    if (Number.isFinite(season) && season > 0) {
        return `S${season}E${episode}`;
    }

    return `E${episode}`;
};

const formatEpisodeIdentity = (ep) => {
    const podcastName = String(ep?.podcast_name || "").trim() || "—";
    const episodeCode = formatEpisodeCodeLabel(ep?.season_number, ep?.episode_number);
    return episodeCode ? `${podcastName} · ${episodeCode}` : podcastName;
};

const isDistributionEnabled = (value) => {
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value).trim().toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    return true;
};

const formatTime = (t) => {
    if (!t || typeof t !== "string") return "";
    if (t.includes(":")) {
        const parts = t.split(":");
        if (parts.length >= 2) {
            return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        }
    }
    if (/^\d{4}$/.test(t)) {
        return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
    }
    return "";
};

// ---- DATETIME FORMATTER (for decisions) ----
const formatDateTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;

    return dt.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// ---- STATUS COLOUR PILL (same as Episodes.jsx) ----
const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();

    switch (s) {
        case "planned":
            return { bg: "#e0f2fe", text: "#0369a1" };
        case "pending approval":
            return { bg: "#fef9c3", text: "#a16207" };
        case "scheduled":
            return { bg: "#dcfce7", text: "#166534" };
        case "approved":
            return { bg: "#d9f99d", text: "#4d7c0f" };
        case "published":
            return { bg: "#bbf7d0", text: "#15803d" };
        case "rejected":
            return { bg: "#fee2e2", text: "#b91c1c" };
        default:
            return { bg: "#e5e7eb", text: "#374151" };
    }
};

// ---- BUILD SAFE PUBLISH DATETIME (avoids NaN) ----
const buildPublishDateTime = (ep) => {
    if (!ep || !ep.publication_date) return null;

    // Always slice to YYYY-MM-DD in case we were given a full ISO string
    const datePart = String(ep.publication_date).slice(0, 10); // e.g. "2026-04-01"

    // Normalise time → HH:MM (fallback 00:00)
    const rawTime =
        typeof ep.publication_time === "string" ? ep.publication_time : "";
    let timePart = "";

    if (rawTime.includes(":")) {
        const parts = rawTime.split(":");
        const hh = (parts[0] || "00").padStart(2, "0");
        const mm = (parts[1] || "00").padStart(2, "0");
        timePart = `${hh}:${mm}`;
    } else if (/^\d{4}$/.test(rawTime)) {
        // "0930" → "09:30"
        timePart = `${rawTime.slice(0, 2)}:${rawTime.slice(2, 4)}`;
    } else {
        // If no usable time, assume midnight
        timePart = "00:00";
    }

    const isoLike = `${datePart}T${timePart}:00`;
    const dt = new Date(isoLike);

    if (Number.isNaN(dt.getTime())) {
        return null;
    }

    return dt;
};

function Dashboard({ onLogout, embedded = false, beta = false }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [stats, setStats] = useState({
        clients: 0,
        podcasts: 0,
        episodes: 0,
        totalMonthlyCost: 0,
    });

    const [attentionEpisodes, setAttentionEpisodes] = useState([]);
    const [recentDecisions, setRecentDecisions] = useState([]);
    const [requestedEpisodes, setRequestedEpisodes] = useState([]);
    const [releaseScheduleDigest, setReleaseScheduleDigest] = useState(null);
    const [releaseScheduleError, setReleaseScheduleError] = useState("");

    useEffect(() => {
        async function load() {
            try {
                const summary = await getDashboardSummary();
                if (!summary?.ok) {
                    throw new Error(summary?.message || "Failed to load dashboard summary");
                }

                setReleaseScheduleDigest(summary.release_schedule_digest || null);
                setReleaseScheduleError("");

                setStats({
                    clients: Number(summary.stats?.clients || 0),
                    podcasts: Number(summary.stats?.podcasts || 0),
                    episodes: Number(summary.stats?.episodes || 0),
                    totalMonthlyCost: Number(summary.stats?.total_monthly_cost || 0),
                });
                setAttentionEpisodes(summary.attention_episodes || []);
                setRecentDecisions(summary.recent_decisions || []);
                setRequestedEpisodes(summary.requested_episodes || []);
            } catch (err) {
                console.error(err);
                setReleaseScheduleDigest(null);
                setReleaseScheduleError("Failed to load release schedule");
                setError("Failed to load dashboard data");
            } finally {
                setLoading(false);
            }
        }

        load();
    }, []);

    // ---- COUNTDOWN FORMATTER (months / weeks / days / hours) ----
    const getCountdown = (ep) => {
        const dt = buildPublishDateTime(ep);
        if (!dt) return null;

        const now = new Date();
        const diffMs = dt.getTime() - now.getTime();
        if (!Number.isFinite(diffMs)) {
            return null;
        }

        const MS_MIN = 60 * 1000;
        const MS_HOUR = 60 * MS_MIN;
        const MS_DAY = 24 * MS_HOUR;

        // Already in the past → overdue
        if (diffMs <= 0) {
            const overdueMs = Math.abs(diffMs);
            const overdueDays = Math.floor(overdueMs / MS_DAY);

            if (overdueDays <= 0) {
                return "Overdue";
            }
            return `${overdueDays}d overdue`;
        }

        const totalDays = Math.floor(diffMs / MS_DAY);

        // Long-range: show months + weeks/days (rough 30-day months)
        if (totalDays >= 60) {
            const months = Math.floor(totalDays / 30);
            const remainingDays = totalDays % 30;

            if (remainingDays >= 14) {
                const weeks = Math.floor(remainingDays / 7);
                return weeks > 0
                    ? `${months}mo ${weeks}w remaining`
                    : `${months}mo remaining`;
            }

            if (remainingDays > 0) {
                return `${months}mo ${remainingDays}d remaining`;
            }

            return `${months}mo remaining`;
        }

        // Medium-range: weeks + days
        if (totalDays >= 14) {
            const weeks = Math.floor(totalDays / 7);
            const days = totalDays % 7;

            if (days > 0) {
                return `${weeks}w ${days}d remaining`;
            }
            return `${weeks}w remaining`;
        }

        // Short-range: days
        if (totalDays >= 2) {
            return `${totalDays}d remaining`;
        }

        // Very short-range: hours and minutes
        const totalMinutes = Math.round(diffMs / MS_MIN);
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;

        if (!Number.isFinite(hours) || !Number.isFinite(mins)) {
            return null;
        }

        if (hours <= 0 && mins <= 0) return "Deadline soon";
        if (hours <= 0) return `${mins}m remaining`;

        return `${hours}h ${mins}m remaining`;
    };

    // Navigate to episodes page with modal open
    const openEpisode = (id) => {
        window.location.hash = buildPortalHash(`episodes/open?id=${id}`);
    };

    // Nicely formatted £ value
    const monthlyCostDisplay =
        typeof stats.totalMonthlyCost === "number"
            ? stats.totalMonthlyCost.toLocaleString("en-GB", {
                style: "currency",
                currency: "GBP",
            })
            : "£0.00";

    const releaseScheduleIssueCount = Number(
        releaseScheduleDigest?.issue_count || 0
    );
    const visibleReleaseScheduleDays = (releaseScheduleDigest?.days || []).filter(
        (day) => Array.isArray(day.items) && day.items.length > 0
    );
    const betaReleaseScheduleRows = visibleReleaseScheduleDays.flatMap((day) => (
        (day.items || []).map((item) => {
            const scheduledEpisodes = item.scheduled_episodes || [];
            return {
                key: `${day.date}-${item.podcast_id}`,
                title: item.podcast_name || `Podcast #${item.podcast_id}`,
                subtitle: scheduledEpisodes.length
                    ? scheduledEpisodes.map((episode) => episode.title || "Untitled episode").join(", ")
                    : "No scheduled or published episode",
                status: item.ok ? "Scheduled" : "Missing",
                statusTone: item.ok ? "success" : "danger",
                date: formatScheduleDayLabel(day.date, day.label),
            };
        })
    ));
    const betaAttentionRows = attentionEpisodes.map((ep) => {
        const countdown = getCountdown(ep);
        const usesPublication = isDistributionEnabled(ep?.podcast_distribution);
        const dateStr = formatPublishDateLabel(ep.publication_date);
        const timeStr = formatTime(ep.publication_time);
        return {
            key: ep.id,
            title: ep.title,
            subtitle: formatEpisodeIdentity(ep),
            status: ep.status || "Draft",
            statusTone: getBetaDashboardStatusTone(ep.status),
            date: ep.publication_date
                ? `${usesPublication ? "Publishes" : "Deadline"}: ${dateStr}${timeStr ? ` at ${timeStr}` : ""}`
                : usesPublication ? "No publication date set" : "No deadline date set",
            dateDetail: countdown,
            onClick: () => openEpisode(ep.id),
        };
    });
    const betaRequestedRows = requestedEpisodes.map((ep) => {
        const usesPublication = isDistributionEnabled(ep?.podcast_distribution);
        const dateStr = formatPublishDateLabel(ep.publication_date);
        const timeStr = formatTime(ep.publication_time);
        return {
            key: ep.id,
            title: ep.title,
            subtitle: formatEpisodeIdentity(ep),
            status: ep.status || "Requested",
            statusTone: getBetaDashboardStatusTone(ep.status || "Requested"),
            date: ep.publication_date
                ? `${usesPublication ? "Publishes" : "Deadline"}: ${dateStr}${timeStr ? ` at ${timeStr}` : ""}`
                : usesPublication ? "No publication date set" : "No deadline date set",
            onClick: () => openEpisode(ep.id),
        };
    });
    const betaDecisionRows = recentDecisions.map((ep) => ({
        key: ep.id,
        title: ep.title,
        subtitle: formatEpisodeIdentity(ep),
        status: ep.status || "Draft",
        statusTone: getBetaDashboardStatusTone(ep.status),
        date: ep.client_reviewed_at ? formatDateTime(ep.client_reviewed_at) : "—",
        dateDetail: "Client decision",
        onClick: () => openEpisode(ep.id),
    }));

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className={`portal-page${beta ? " portal-page--beta-dashboard" : ""}`}>
                {/* Header */}
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Dashboard</h2>
                        <p className="portal-page__subtitle">
                            Overview & upcoming deadlines.
                        </p>
                    </div>
                </div>

                {error && <div className="alert alert--error">{error}</div>}
                {loading && <p>Loading dashboard…</p>}

                {!loading && !error && (
                    beta ? (
                        <div className="beta-dashboard-stack">
                            <BetaDashboardStats
                                items={[
                                    { label: "Clients", value: stats.clients, description: "Active clients" },
                                    { label: "Podcasts", value: stats.podcasts, description: "Active podcasts" },
                                    { label: "Episodes", value: stats.episodes, description: "All-time episodes" },
                                    { label: "Income", value: monthlyCostDisplay, description: "Total monthly income" },
                                ]}
                            />

                            <BetaDashboardSection
                                title="Podcast Release Schedule"
                                description="Live 7-day view of expected releases and scheduled or published coverage."
                                badge={releaseScheduleDigest ? {
                                    label: releaseScheduleIssueCount === 0
                                        ? "All scheduled"
                                        : `${releaseScheduleIssueCount} issue${releaseScheduleIssueCount === 1 ? "" : "s"}`,
                                    tone: releaseScheduleIssueCount === 0 ? "success" : "danger",
                                } : null}
                            >
                                {releaseScheduleError ? (
                                    <div className="alert alert--error">{releaseScheduleError}</div>
                                ) : (
                                    <BetaDashboardTable
                                        ariaLabel="Podcast release schedule"
                                        columns={["Podcast", "Status", "Date"]}
                                        rows={betaReleaseScheduleRows}
                                        emptyMessage={releaseScheduleDigest
                                            ? "No releases expected in the next 7 days."
                                            : "No release schedule data available."}
                                        itemLabel="releases"
                                    />
                                )}
                            </BetaDashboardSection>

                            <BetaDashboardSection
                                title="Episodes Requiring Attention"
                                description="Upcoming, overdue or rejected episodes which still need action."
                            >
                                <BetaDashboardTable
                                    ariaLabel="Episodes requiring attention"
                                    rows={betaAttentionRows}
                                    emptyMessage="No episodes need attention."
                                    itemLabel="episodes"
                                />
                            </BetaDashboardSection>

                            <BetaDashboardSection
                                title="Requested Episodes"
                                description="Episodes currently waiting in Requested status."
                            >
                                <BetaDashboardTable
                                    ariaLabel="Requested episodes"
                                    rows={betaRequestedRows}
                                    emptyMessage="No episodes are currently in Requested."
                                    itemLabel="episodes"
                                />
                            </BetaDashboardSection>

                            <BetaDashboardSection
                                title="Recent Approvals & Rejections"
                                description="Client decisions from the last seven days which have not moved to scheduling or publishing."
                            >
                                <BetaDashboardTable
                                    ariaLabel="Recent approvals and rejections"
                                    rows={betaDecisionRows}
                                    emptyMessage="No recent approvals or rejections in the last seven days."
                                    itemLabel="decisions"
                                />
                            </BetaDashboardSection>
                        </div>
                    ) : (
                    <>
                        {/* Stats */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(220px, 1fr))",
                                gap: "16px",
                                marginTop: "8px",
                            }}
                        >
                            <div className="card">
                                <h3 className="card__title">Clients</h3>
                                <p className="card__subtitle">Active clients</p>
                                <div className="stat-number">{stats.clients}</div>
                            </div>

                            <div className="card">
                                <h3 className="card__title">Podcasts</h3>
                                <p className="card__subtitle">Active podcasts</p>
                                <div className="stat-number">{stats.podcasts}</div>
                            </div>

                            <div className="card">
                                <h3 className="card__title">Episodes</h3>
                                <p className="card__subtitle">All-time episodes</p>
                                <div className="stat-number">{stats.episodes}</div>
                            </div>

                            {/* Total monthly cost card */}
                            <div className="card">
                                <h3 className="card__title">Income</h3>
                                <p className="card__subtitle">
                                    Total monthly income
                                </p>
                                <div className="stat-number">
                                    {monthlyCostDisplay}
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ marginTop: 20 }}>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    flexWrap: "wrap",
                                }}
                            >
                                <div>
                                    <h3 className="card__title">
                                        Podcast Release Schedule
                                    </h3>
                                    <p className="card__subtitle">
                                        Live 7-day view of expected releases and
                                        scheduled/published coverage.
                                    </p>
                                </div>
                                {releaseScheduleDigest && (
                                    <span
                                        className="pill"
                                        style={{
                                            background:
                                                releaseScheduleIssueCount > 0
                                                    ? "#fee2e2"
                                                    : "#dcfce7",
                                            color:
                                                releaseScheduleIssueCount > 0
                                                    ? "#b91c1c"
                                                    : "#166534",
                                        }}
                                    >
                                        {releaseScheduleIssueCount === 0
                                            ? "All scheduled"
                                            : `${releaseScheduleIssueCount} issue${
                                                  releaseScheduleIssueCount === 1
                                                      ? ""
                                                      : "s"
                                              }`}
                                    </span>
                                )}
                            </div>

                            {releaseScheduleError && (
                                <div className="alert alert--error">
                                    {releaseScheduleError}
                                </div>
                            )}

                            {!releaseScheduleError && !releaseScheduleDigest && (
                                <p style={{ marginTop: 10, color: "#6b7280" }}>
                                    No release schedule data available.
                                </p>
                            )}

                            {releaseScheduleDigest && (
                                <div className="episode-list" style={{ marginTop: 12 }}>
                                    {visibleReleaseScheduleDays.length === 0 && (
                                        <p style={{ marginTop: 10, color: "#6b7280" }}>
                                            No releases expected in the next 7 days.
                                        </p>
                                    )}
                                    {visibleReleaseScheduleDays.map((day) => {
                                        const dayHasIssue = (day.items || []).some(
                                            (item) => !item.ok
                                        );
                                        return (
                                            <div
                                                key={day.date}
                                                className="episode-card"
                                                style={{
                                                    borderLeftColor: dayHasIssue
                                                        ? "#dc2626"
                                                        : "#16a34a",
                                                    cursor: "default",
                                                }}
                                            >
                                                <div className="episode-card__main">
                                                    <h3 className="episode-card__title">
                                                        {formatScheduleDayLabel(
                                                            day.date,
                                                            day.label
                                                        )}
                                                    </h3>
                                                    <div className="episode-card__podcast">
                                                        {Number(day.expected_count || 0)} expected
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gap: 8,
                                                            marginTop: 10,
                                                        }}
                                                    >
                                                        {(day.items || []).map((item) => {
                                                            const episodes =
                                                                item.scheduled_episodes || [];
                                                            return (
                                                                <div
                                                                    key={`${day.date}-${item.podcast_id}`}
                                                                    style={{
                                                                        display: "grid",
                                                                        gridTemplateColumns:
                                                                            "minmax(0, 1fr) auto",
                                                                        gap: 10,
                                                                        alignItems:
                                                                            "flex-start",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <div>
                                                                        <strong>
                                                                            {item.podcast_name ||
                                                                                `Podcast #${item.podcast_id}`}
                                                                        </strong>
                                                                        <div className="episode-card__meta">
                                                                            {episodes.length
                                                                                ? episodes
                                                                                      .map(
                                                                                          (episode) =>
                                                                                              `${episode.title || "Untitled episode"}${
                                                                                                  episode.status
                                                                                                      ? ` (${episode.status})`
                                                                                                      : ""
                                                                                              }`
                                                                                      )
                                                                                      .join(", ")
                                                                                : "No scheduled or published episode"}
                                                                        </div>
                                                                    </div>
                                                                    <span
                                                                        className="pill"
                                                                        style={{
                                                                            background: item.ok
                                                                                ? "#dcfce7"
                                                                                : "#fee2e2",
                                                                            color: item.ok
                                                                                ? "#166534"
                                                                                : "#b91c1c",
                                                                            justifySelf: "end",
                                                                        }}
                                                                    >
                                                                        {item.ok
                                                                            ? "Scheduled"
                                                                            : "Missing"}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Needs Attention */}
                        <div className="card" style={{ marginTop: 20 }}>
                            <h3 className="card__title">
                                Episodes Requiring Attention
                            </h3>
                            <p className="card__subtitle">
                                Upcoming/overdue episodes that aren&apos;t yet
                                scheduled/approved/published, plus any rejected episodes.
                            </p>

                            {attentionEpisodes.length === 0 && (
                                <p style={{ marginTop: 10, color: "#6b7280" }}>
                                    No episodes need attention 🎉
                                </p>
                            )}

                            <div className="episode-list" style={{ marginTop: 12 }}>
                                {attentionEpisodes.map((ep) => {
                                    const countdown = getCountdown(ep);
                                    const dateStr = formatPublishDateLabel(
                                        ep.publication_date
                                    );
                                    const timeStr = formatTime(ep.publication_time);
                                    const usesPublication = isDistributionEnabled(
                                        ep?.podcast_distribution
                                    );
                                    const statusColors = getStatusColor(ep.status);

                                    return (
                                        <div
                                            key={ep.id}
                                            className="episode-card"
                                            style={{
                                                borderLeftColor: "#e63946",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => openEpisode(ep.id)}
                                        >
                                            <div className="episode-card__main">
                                                <h3 className="episode-card__title">
                                                    {ep.title}
                                                </h3>
                                                <div className="episode-card__podcast">
                                                    {(() => {
                                                        const podcastName = String(
                                                            ep.podcast_name || ""
                                                        ).trim();
                                                        const episodeCode = formatEpisodeCodeLabel(
                                                            ep.season_number,
                                                            ep.episode_number
                                                        );
                                                        if (!podcastName) return "—";
                                                        return episodeCode
                                                            ? `${podcastName} · ${episodeCode}`
                                                            : podcastName;
                                                    })()}
                                                </div>
                                                <div className="episode-card__meta">
                                                    {ep.publication_date ? (
                                                        <>
                                                            {usesPublication
                                                                ? `Publishes ${dateStr}${timeStr ? ` at ${timeStr}` : ""}`
                                                                : `Deadline ${dateStr}`}
                                                            {countdown
                                                                ? ` • ${countdown}`
                                                                : ""}
                                                        </>
                                                    ) : (
                                                        usesPublication
                                                            ? "No publication date set"
                                                            : "No deadline date set"
                                                    )}
                                                </div>
                                            </div>
                                            <div className="episode-card__right">
                                                <span
                                                    className="pill pill--role-client"
                                                    style={{
                                                        background: statusColors.bg,
                                                        color: statusColors.text,
                                                    }}
                                                >
                                                    {ep.status || "Draft"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Requested episodes */}
                        <div className="card" style={{ marginTop: 20 }}>
                            <h3 className="card__title">Requested Episodes</h3>
                            <p className="card__subtitle">
                                Episodes currently waiting in Requested status.
                            </p>

                            {requestedEpisodes.length === 0 && (
                                <p style={{ marginTop: 10, color: "#6b7280" }}>
                                    No episodes are currently in Requested.
                                </p>
                            )}

                            <div className="episode-list" style={{ marginTop: 12 }}>
                                {requestedEpisodes.map((ep) => {
                                    const statusColors = getStatusColor(ep.status);
                                    const dateStr = formatPublishDateLabel(
                                        ep.publication_date
                                    );
                                    const timeStr = formatTime(ep.publication_time);
                                    const usesPublication = isDistributionEnabled(
                                        ep?.podcast_distribution
                                    );

                                    return (
                                        <div
                                            key={ep.id}
                                            className="episode-card"
                                            style={{
                                                borderLeftColor: "#6d28d9",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => openEpisode(ep.id)}
                                        >
                                            <div className="episode-card__main">
                                                <h3 className="episode-card__title">
                                                    {ep.title}
                                                </h3>
                                                <div className="episode-card__podcast">
                                                    {(() => {
                                                        const podcastName = String(
                                                            ep.podcast_name || ""
                                                        ).trim();
                                                        const episodeCode = formatEpisodeCodeLabel(
                                                            ep.season_number,
                                                            ep.episode_number
                                                        );
                                                        if (!podcastName) return "—";
                                                        return episodeCode
                                                            ? `${podcastName} · ${episodeCode}`
                                                            : podcastName;
                                                    })()}
                                                </div>
                                                <div className="episode-card__meta">
                                                    {ep.publication_date
                                                        ? usesPublication
                                                            ? `Publishes ${dateStr}${timeStr ? ` at ${timeStr}` : ""}`
                                                            : `Deadline ${dateStr}`
                                                        : usesPublication
                                                            ? "No publication date set"
                                                            : "No deadline date set"}
                                                </div>
                                            </div>
                                            <div className="episode-card__right">
                                                <span
                                                    className="pill pill--role-client"
                                                    style={{
                                                        background: statusColors.bg,
                                                        color: statusColors.text,
                                                    }}
                                                >
                                                    {ep.status || "Draft"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Recent approvals/rejections */}
                        <div className="card" style={{ marginTop: 20 }}>
                            <h3 className="card__title">
                                Recent Approvals & Rejections
                            </h3>
                            <p className="card__subtitle">
                                Episodes approved or rejected in the last 7 days
                                that haven&apos;t yet moved on to scheduling or
                                publishing.
                            </p>

                            {recentDecisions.length === 0 && (
                                <p style={{ marginTop: 10, color: "#6b7280" }}>
                                    No recent approvals or rejections in the last 7 days.
                                </p>
                            )}

                            <div className="episode-list" style={{ marginTop: 12 }}>
                                {recentDecisions.map((ep) => {
                                    const statusColors = getStatusColor(ep.status);
                                    const decidedAt = ep.client_reviewed_at
                                        ? formatDateTime(ep.client_reviewed_at)
                                        : "";

                                    return (
                                        <div
                                            key={ep.id}
                                            className="episode-card"
                                            style={{
                                                borderLeftColor:
                                                    (ep.status || "")
                                                        .toLowerCase() === "approved"
                                                        ? "#16a34a"
                                                        : "#dc2626",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => openEpisode(ep.id)}
                                        >
                                            <div className="episode-card__main">
                                                <h3 className="episode-card__title">
                                                    {ep.title}
                                                </h3>
                                                <div className="episode-card__podcast">
                                                    {(() => {
                                                        const podcastName = String(
                                                            ep.podcast_name || ""
                                                        ).trim();
                                                        const episodeCode = formatEpisodeCodeLabel(
                                                            ep.season_number,
                                                            ep.episode_number
                                                        );
                                                        if (!podcastName) return "—";
                                                        return episodeCode
                                                            ? `${podcastName} · ${episodeCode}`
                                                            : podcastName;
                                                    })()}
                                                </div>
                                                <div className="episode-card__meta">
                                                    {decidedAt
                                                        ? `${ep.status} on ${decidedAt}`
                                                        : ep.status}
                                                </div>
                                            </div>
                                            <div className="episode-card__right">
                                                <span
                                                    className="pill pill--role-client"
                                                    style={{
                                                        background: statusColors.bg,
                                                        color: statusColors.text,
                                                    }}
                                                >
                                                    {ep.status || "Draft"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                    )
                )}
            </div>
        </PageShell>
    );
}

export default Dashboard;
